// ============================================================
// ピースビルダー トップ一覧 ダミーデータ（UI 確認用）
//
// 全状態・全コンポーネントを画面上で目視確認できるよう網羅的に用意。
// 実 API 接続は後続フェーズ（このファイルを /v1/pieces レスポンスへ差し替え）。
// 状態別表現の検証ポイントは docs/piece-builder/mock/card-states.html を参照。
// ============================================================

import { PieceStatus } from "features/builder/types";
import { MockPiece } from "features/builder/pieceList";

const EMPTY_REACTION = {
  difficult: 0,
  easy: 0,
  cool: 0,
  fun: 0,
  amazing: 0,
  bad: 0,
};

// 既定値を埋める factory（必要なフィールドだけ上書き）
const makePiece = (
  partial: Partial<MockPiece> & Pick<MockPiece, "piece_id" | "name">,
): MockPiece => ({
  inquiry_id: "PIECE_BUILDER",
  description: "",
  tags: "",
  capture: null,
  cover: null,
  author_uid: "me",
  copied_piece_id: null,
  original_piece_id: null,
  original_author_uid: null,
  original_piece_created_at: null,
  status: PieceStatus.PUBLISHED,
  status_before_delete: null,
  completed: true,
  completed_at: "2026-05-10 10:00:00",
  published: true,
  enable_copied: false,
  is_template: false,
  version: 1,
  liked: 0,
  viewed: 0,
  copied: 0,
  played: 0,
  cleared: 0,
  reaction: EMPTY_REACTION,
  created_at: "2026-05-01 10:00:00",
  updated_at: "2026-05-10 10:00:00",
  published_at: "2026-05-10 10:00:00",
  deleted_at: null,
  author_name: "わたし",
  author_icon: null,
  original_author_name: null,
  original_author_icon: null,
  is_liked: false,
  ...partial,
});

// ── じぶんのピース（PieceListContext.MINE）──
// PUBLISHED / DRAFT_NEW / EDITING / PUBLISHED+remix / UNPUBLISHED を網羅
export const MOCK_MY_PIECES: MockPiece[] = [
  makePiece({
    piece_id: "mine-1",
    name: "森のまがり道",
    description: "もりの なかを まがりくねった みちを すすもう！",
    emoji: "🏞️",
    status: PieceStatus.PUBLISHED,
    viewed: 234,
    liked: 18,
    played: 56,
    updated_at: "2026-05-16 12:00:00",
  }),
  makePiece({
    piece_id: "mine-2",
    name: "あたらしい ピース 1",
    description: "つくりかけの ピース",
    emoji: "🧊",
    status: PieceStatus.DRAFT_NEW,
    completed: false,
    completed_at: null,
    published: false,
    published_at: null,
    updated_at: "2026-05-15 09:30:00",
  }),
  makePiece({
    piece_id: "mine-3",
    name: "おしろの ぼうけん",
    description: "おしろの なかを たんけんしよう",
    emoji: "🏰",
    status: PieceStatus.EDITING,
    viewed: 1234,
    liked: 89,
    played: 320,
    version: 2,
    updated_at: "2026-05-14 18:00:00",
  }),
  makePiece({
    piece_id: "mine-4",
    name: "うみの ぼうけん",
    description: "うみの せかいを つくってみた（リミックス）",
    emoji: "🏝️",
    status: PieceStatus.PUBLISHED,
    copied_piece_id: "public-4",
    original_piece_id: "public-4",
    original_author_uid: "user-haru",
    original_author_name: "ハル",
    viewed: 67,
    liked: 5,
    played: 12,
    updated_at: "2026-05-12 15:00:00",
  }),
  makePiece({
    piece_id: "mine-5",
    name: "うみの きほん",
    description: "うみステージの きほんマップ",
    emoji: "🌊",
    status: PieceStatus.PUBLISHED,
    viewed: 56,
    liked: 4,
    played: 8,
    updated_at: "2026-05-08 11:00:00",
  }),
  makePiece({
    piece_id: "mine-6",
    name: "もりの ひみつ",
    description: "こうかいを とりさげた ピース",
    emoji: "🌳",
    status: PieceStatus.UNPUBLISHED,
    status_before_delete: null,
    published: false,
    published_at: null,
    viewed: 45,
    liked: 0,
    played: 3,
    updated_at: "2026-05-05 10:00:00",
  }),
];

// ── みんなのピース（PieceListContext.PUBLIC）──
// 公式(is_template)×2 / 一般 / リミックス / k 表記確認用の大きな数値
export const MOCK_PUBLIC_PIECES: MockPiece[] = [
  makePiece({
    piece_id: "public-1",
    name: "はじめての めいろ",
    description: "はじめてでも あそべる かんたんめいろ",
    emoji: "🌟",
    author_uid: "official",
    author_name: "まなパズ公式",
    is_template: true,
    enable_copied: true,
    tags: "めいろ,かんたん",
    viewed: 5600,
    liked: 423,
    played: 1200,
    updated_at: "2026-05-17 10:00:00",
  }),
  makePiece({
    piece_id: "public-2",
    name: "ドラゴンの たから",
    description: "ドラゴンが まもる たからを さがそう",
    emoji: "🐉",
    author_uid: "official",
    author_name: "まなパズ公式",
    is_template: true,
    enable_copied: true,
    tags: "たんけん",
    viewed: 8900,
    liked: 765,
    played: 4300,
    updated_at: "2026-05-16 10:00:00",
  }),
  makePiece({
    piece_id: "public-3",
    name: "きつねの おうち",
    description: "きつねの おうちまで いこう",
    emoji: "🦊",
    author_uid: "user-ayaka",
    author_name: "アヤカ",
    tags: "もり",
    viewed: 234,
    liked: 18,
    played: 90,
    updated_at: "2026-05-15 10:00:00",
  }),
  makePiece({
    piece_id: "public-4",
    name: "おしろの ぼうけん 2",
    description: "おしろシリーズの つづき（リミックス）",
    emoji: "🏰",
    author_uid: "user-haru",
    author_name: "ハル",
    copied_piece_id: "public-2",
    original_piece_id: "public-2",
    original_author_uid: "official",
    original_author_name: "まなパズ公式",
    tags: "たんけん",
    viewed: 567,
    liked: 45,
    played: 210,
    updated_at: "2026-05-14 10:00:00",
  }),
  makePiece({
    piece_id: "public-5",
    name: "かめさんと さんぽ",
    description: "かめさんと ゆっくり さんぽ",
    emoji: "🐢",
    author_uid: "user-nanami",
    author_name: "ナナミ",
    tags: "うみ",
    viewed: 89,
    liked: 7,
    played: 30,
    updated_at: "2026-05-13 10:00:00",
  }),
  makePiece({
    piece_id: "public-6",
    name: "ユニコーンと たびする",
    description: "ユニコーンと そらの たびへ",
    emoji: "🦄",
    author_uid: "user-sakura",
    author_name: "サクラ",
    tags: "そら",
    viewed: 1100,
    liked: 98,
    played: 540,
    updated_at: "2026-05-12 10:00:00",
  }),
];

// ── ゴミばこ（PieceListContext.TRASH）──
// 残日数 = 30 - (今日 - deleted_at)。28/20/15/10/5/2 を網羅（≤7 は点滅）
// 基準日（mock 上の「今日」）: 2026-05-18
export const MOCK_TRASH_PIECES: MockPiece[] = [
  makePiece({
    piece_id: "trash-1",
    name: "もりの まいご",
    emoji: "🏞️",
    status: PieceStatus.REMOVED,
    status_before_delete: PieceStatus.PUBLISHED,
    deleted_at: "2026-05-16 12:00:00", // あと 28 にち
  }),
  makePiece({
    piece_id: "trash-2",
    name: "きつねの ぼうけん",
    emoji: "🦊",
    status: PieceStatus.REMOVED,
    status_before_delete: PieceStatus.DRAFT_NEW,
    deleted_at: "2026-05-08 12:00:00", // あと 20 にち
  }),
  makePiece({
    piece_id: "trash-3",
    name: "よぞらの たんけん",
    emoji: "🌌",
    status: PieceStatus.REMOVED,
    status_before_delete: PieceStatus.PUBLISHED,
    deleted_at: "2026-05-03 12:00:00", // あと 15 にち
  }),
  makePiece({
    piece_id: "trash-4",
    name: "うみの ふしぎ",
    emoji: "🌊",
    status: PieceStatus.REMOVED,
    status_before_delete: PieceStatus.UNPUBLISHED,
    deleted_at: "2026-04-28 12:00:00", // あと 10 にち
  }),
  makePiece({
    piece_id: "trash-5",
    name: "かざんの ぼうけん",
    emoji: "🌋",
    status: PieceStatus.REMOVED,
    status_before_delete: PieceStatus.PUBLISHED,
    deleted_at: "2026-04-23 12:00:00", // あと 5 にち（点滅）
  }),
  makePiece({
    piece_id: "trash-6",
    name: "おしろの れんしゅう",
    emoji: "🏰",
    status: PieceStatus.REMOVED,
    status_before_delete: PieceStatus.DRAFT_NEW,
    deleted_at: "2026-04-20 12:00:00", // あと 2 にち（点滅）
  }),
];

// ゴミ箱の自動消去までの日数（30 日保持）
export const TRASH_RETENTION_DAYS = 30;
// 残日数がこの値以下で警告色 + 点滅
export const TRASH_WARNING_DAYS = 7;
